import { CSSProperties } from 'react';

// Eased grow/shrink between the side dock and fullscreen: we animate the real
// geometry (not a transform scale) plus the base colour.
export const EXPAND_TRANSITION = ['width', 'height', 'right', 'bottom', 'border-radius', 'background-color']
  .map((prop) => `${prop} 300ms cubic-bezier(0.16, 1, 0.3, 1)`)
  .join(', ');

// Kept in sync with the content pane's --chat-w reflow (420ms, globals.css
// .orion-content-pane) so the panel slides out in lockstep with the content
// reclaiming the space.
export const CHAT_CLOSE_MS = 420;

// The docked chat's width. Shared with AppFrame, which sets --chat-w to this so
// the content pane's inline-end edge reflows in lockstep as the chat slides in.
export const CHAT_DOCK_WIDTH = 370;

// Open (wide): a right-docked column that PUSHES the content pane rather than
// overlaying it. AppFrame's --chat-w drives the content reflow.
export const EXPANDED_WIDE: CSSProperties = {
  position: 'fixed',
  zIndex: 50,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  top: 0,
  bottom: 0,
  insetInlineEnd: 0,
  width: CHAT_DOCK_WIDTH,
  height: '100dvh',
  backgroundColor: 'var(--field-color)',
  color: 'rgba(var(--ink-rgb),0.95)',
  borderInlineStart: '1px solid rgba(var(--hairline-rgb),0.10)',
  boxShadow: '-8px 0 28px rgba(var(--ds-black-rgb), 0.06)',
  borderRadius: 0,
  animation: 'orion-dock-in 420ms cubic-bezier(0.32, 0.72, 0, 1)',
};

// Fullscreen (wide): the chat grown to fill everything inline-end of the sidebar,
// which stays visible and pushes the chat via --sidebar-w. Sized by width so the
// dock↔fullscreen resize animates the same width property (no auto-inset jump).
export const FULLSCREEN_WIDE: CSSProperties = {
  position: 'fixed',
  zIndex: 50,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  top: 0,
  bottom: 0,
  insetInlineEnd: 0,
  width: 'calc(100vw - var(--sidebar-w, 0px))',
  height: '100dvh',
  backgroundColor: 'var(--field-color)',
  color: 'rgba(var(--ink-rgb),0.95)',
  borderInlineStart: '1px solid rgba(var(--hairline-rgb),0.10)',
  boxShadow: 'none',
  borderRadius: 0,
};

// Narrow (phone): the chat is the whole screen for every open size. Transparent so
// the app's CosmicBackdrop shows through.
export const EXPANDED_NARROW: CSSProperties = {
  position: 'fixed',
  zIndex: 50,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  inset: 0,
  height: '100dvh',
  backgroundColor: 'rgba(var(--surface-base-rgb), 0)',
  border: 'none',
  boxShadow: 'none',
  borderRadius: 0,
  color: 'rgba(var(--ink-rgb),0.95)',
  animation: 'orion-sheet-in 320ms cubic-bezier(0.16, 1, 0.3, 1)',
};

// Figma chat-header spec: 24px top · 20px inline · 8px bottom (Spacing/24, 20, 8).
export const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  padding: '24px 20px 8px',
  flexShrink: 0,
};

export const maestroTitleStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  minWidth: 0,
  padding: '4px 6px',
};

export const maestroNameStyle: CSSProperties = {
  fontFamily: 'var(--brand-font-display)',
  fontSize: 20,
  fontWeight: 800,
  letterSpacing: '-0.03em',
  color: 'rgba(var(--ink-rgb),0.92)',
  whiteSpace: 'nowrap',
};

const titleTextStyle: CSSProperties = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  flex: 1,
  minWidth: 0,
};

// Figma "Header" 14701:127253 — the chat title is Small/Medium: Wix Madefor Medium 14/18, primary
// ink, -2% tracking (was semibold/secondary).
export const newChatTitleStyle: CSSProperties = {
  ...titleTextStyle,
  padding: '4px 6px',
  fontSize: 14,
  lineHeight: '18px',
  fontWeight: 'var(--sym-weight-medium)',
  letterSpacing: '-0.02em',
  color: 'var(--sym-ink)',
};

